import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { ToolCard as ToolCardData } from '@/utils/api-pb';

// Text colours darkened from the plan's sample palette to clear WCAG AA (4.5:1) for
// small text against the light backgrounds — ratios verified in task-12-report.md.
const TONE = {
  good: 'bg-[#E8F7EE] text-[#136C3A]',
  warn: 'bg-[#FFF4E0] text-[#7A4A00]',
  muted: 'bg-[var(--grey-background,#f8f8f8)] text-[color:var(--grey-text,#565858)]',
} as const;

function Badge({ tone, children }: { tone: keyof typeof TONE; children: React.ReactNode }) {
  return (
    <span className={cn('rounded-full px-2.5 py-1 font-segoe text-[12px] font-medium', TONE[tone])}>
      {children}
    </span>
  );
}

/** RU-market badges for a tool card: access from Russia, payment method, and language/origin. */
export function RuBadges({ tool }: { tool: ToolCardData }) {
  const { t } = useTranslation('tools');
  const access = tool.ru_access === 'works' ? 'good' : tool.ru_access === 'vpn' ? 'warn' : 'muted';
  const payment = tool.ru_payment === 'rub_card' ? 'good' : tool.ru_payment === 'aggregator' ? 'warn' : 'muted';
  return (
    <div className="flex flex-wrap gap-1.5">
      <Badge tone={access}>{t(`badges.${tool.ru_access}`)}</Badge>
      <Badge tone={payment}>{t(`badges.${tool.ru_payment}`)}</Badge>
      {tool.ru_language && <Badge tone="good">{t('badges.ru_language')}</Badge>}
      {tool.is_russian && <Badge tone="good">{t('badges.is_russian')}</Badge>}
    </div>
  );
}
